import {
  ContentHubType,
  AlternativeContentHubType,
  CollectionType,
  AlternativeCollectionType,
  LandingType,
} from 'src/constants';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { getUrlPrefix } from 'src/services/http/user';

interface LessonUrlParams {
  lessonSlug: string;
  courseSlug?: string;
  pathSlug?: string;
  sectionSlug?: string;
  query?: string;
}

type MyLearningUrlParams = MyLearningPageContentSections | ':status';

export const LEARNING_PATH_URL_PATH = 'path';
export const COURSE_URL_PATH = 'course';
export const LESSON_URL_PATH = 'lesson';

export const MY_LEARNING_URL_PATH = 'my-learning';
export const CATALOG_URL_PATH = 'catalog';

export const HUB_URL_PATH = 'hub';
export const COLLECTION_URL_PATH = 'collection';

export const LANDING_URL_PATH = '/landing';

export const withPrefix = (url: string, prefix?: string): string =>
  typeof prefix === 'string' ? `${prefix}${url}` : `${getUrlPrefix()}${url}`;

export const stripLeadingSlash = (url: string): string => (url.startsWith('/') ? url.slice(1) : url);

// e.g. getPathUrl(':pathId') -> /path/:pathId
// e.g. getPathUrl(':pathId', true) -> /path/:pathId/survey
// e.g. getPathUrl(':pathId', false, new URLSearchParams('q=1')) -> /path/:pathId?q=1
export const getPathUrl = (pathSlug: string, hasSurvey?: boolean, params?: URLSearchParams): string => {
  const queryParams = params ? `?${params}` : '';

  return `/${LEARNING_PATH_URL_PATH}/${pathSlug}${hasSurvey ? '/survey' : ''}${queryParams}`;
};

// e.g. getCourseUrl(':courseId') -> /course/:courseId
// e.g. getCourseUrl(':courseId', ':pathId') -> /path/:pathId/course/:courseId
// e.g. getCourseUrl(':courseId', '', true) -> /course/:courseId/survey
// e.g. getCourseUrl(':courseId', ':pathId', true) -> /path/:pathId/course/:courseId/survey
// e.g. getCourseUrl(':courseId', undefined, false, new URLSearchParams('foo=bar')) -> /course/:courseId?foo=bar
export const getCourseUrl = (
  courseSlug: string,
  pathSlug?: string,
  hasSurvey?: boolean,
  params?: URLSearchParams,
): string => {
  const learningPathUrl = pathSlug ? getPathUrl(pathSlug) : '';
  const queryParams = params ? `?${params}` : '';

  return `${learningPathUrl}/${COURSE_URL_PATH}/${courseSlug}${hasSurvey ? '/survey' : ''}${queryParams}`;
};

// e.g. getLessonUrl({ lessonSlug: ':lessonId' }) -> /lesson/:lessonId
// e.g. getLessonUrl({ courseSlug: ':courseId', lessonSlug: ':lessonId' }) -> /course/:courseId/lesson/:lessonId
// e.g. getLessonUrl({ pathSlug: ':pathId', courseSlug: ':courseId', lessonSlug: ':lessonId' }) -> /path/:pathId/course/:courseId/lesson/:lessonId
// e.g. getLessonUrl({ lessonSlug: ':lessonId', sectionSlug: 'quiz' }) -> /lesson/:lessonId#quiz
// e.g. getLessonUrl({ lessonSlug: ':lessonId', query: 'foo=bar' }) -> /lesson/:lessonId?foo=bar
export const getLessonUrl = ({ lessonSlug, courseSlug, pathSlug, sectionSlug, query }: LessonUrlParams): string => {
  const courseUrl = courseSlug ? getCourseUrl(courseSlug, pathSlug) : '';
  const sectionFragment = sectionSlug ? `#${sectionSlug}` : '';
  const queryFragment = query ? `?${query}` : '';

  return `${courseUrl}/${LESSON_URL_PATH}/${lessonSlug}${queryFragment}${sectionFragment}`;
};

// e.g. getHubUrl(ContentHubType.LEARNING_PATHS) -> /hub/all-learning-paths
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, 'section1') -> /hub/all-learning-paths#section1
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, undefined, 'some-hub') -> /hub/all-learning-paths/some-hub
// e.g. getHubUrl(ContentHubType.LEARNING_PATHS, undefined, 'some-hub', true) -> /hub/all-learning-paths/some-hub/*
export const getHubUrl = (
  type: ContentHubType | AlternativeContentHubType,
  sectionId?: string,
  hubSlug?: string,
  wildcard?: boolean,
): string => {
  const hubFragment = hubSlug ? `/${hubSlug}` : '';
  const wildcardFragment = wildcard ? '/*' : '';
  const sectionFragment = sectionId ? `#${sectionId}` : '';

  return `/${HUB_URL_PATH}/${type}${hubFragment}${wildcardFragment}${sectionFragment}`;
};

// e.g. getCollectionUrl(CollectionType.TOPIC, ':topicId') -> /collection/topic/:topicId
export const getCollectionUrl = (type: CollectionType | AlternativeCollectionType, collectionSlug?: string): string => {
  const collectionFragment = collectionSlug ? `/${collectionSlug}` : '';

  return `/${COLLECTION_URL_PATH}/${type}${collectionFragment}`;
};

// e.g. getCertificationUrl() -> /certifications
// e.g. getCertificationUrl(':certificationId') -> /certifications/:certificationId
export const getCertificationUrl = (certificationSlug?: string): string => {
  const certificationFragment = certificationSlug ? `/${certificationSlug}` : '';

  return `/${ContentHubType.CERTIFICATIONS}${certificationFragment}`;
};

// e.g. getCatalogUrl() -> /catalog
// e.g. getCatalogUrl('/filter') -> /catalog/filter
export const getCatalogUrl = (filter?: string): string => `/${CATALOG_URL_PATH}${filter || ''}`;

// e.g. getLandingUrl(LandingType.LIVE_TRAINING) -> /live-training
// e.g. withPrefix(getLandingUrl(LandingType.LIVE_TRAINING), LANDING_URL_PATH) -> /landing/live-training
export const getLandingUrl = (type: LandingType, slug?: string): string => {
  const landingFragment = slug ? `/${slug}` : '';

  return `/${type}${landingFragment}`;
};

// e.g. getMyLearningUrl(':status') -> /my-learning/:status
export const getMyLearningUrl = (status: MyLearningUrlParams): string => `/${MY_LEARNING_URL_PATH}/${status}`;
